import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../api/client';
import { setDeepLink } from '../../utils/deepLink'; // Block 203a: carry the nudge's contactId to the target section
import { Button, Card, Skeleton, DashboardCard } from '../../ui';
import { useDelayedDelete } from '../../ui/useDelayedDelete';
import '../dashboard.css';

// ─────────────────────────────────────────────────────────────────────────────
// FOLLOW-UPS (FU-1, 2026-10-07; decisions 41 to 45).
//
// One data source (GET /api/follow-up/nudges: { groups, total }), one card, two places:
//   FollowUpPanel  the Dashboard section: the top 3 contacts, then "See all (N)"
//   FollowUpList   the Contacts page's "Needs follow up (N)" chip: all of them
// A card is ONE contact with every reason it has (decision 42), most urgent first (decision 41,
// ordered by the server). ✕ dismisses that card's follow-ups for good, stored server side for this
// person, behind Undo (Carla's item 10, replacing decision 45's 7-day snooze); Aidey Chat reads the
// same dismissals, so the two agree.
// ─────────────────────────────────────────────────────────────────────────────

// Block B: the urgency colours are tokens. The word in the reason carries the meaning; the colour
// only repeats it. Hover lives in dashboard.css, under (hover: hover) (mobile addendum, rule 8).
const URGENCY_COLOR = { high: 'var(--danger)', medium: 'var(--orange)', low: 'var(--ink-4)' };

function NudgeWarningIcon({ color = URGENCY_COLOR.high, size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" style={{ fill: color, flexShrink: 0, verticalAlign: 'middle' }}>
      <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z" />
    </svg>
  );
}

// Map suggestedAction values to short button labels (non-personalised fallback)
const ACTION_LABEL = {
  'Chase payment': 'Chase payment',
  'Send a gentle reminder': 'Send reminder',
  'Follow up on proposal': 'Follow up',
  'Discuss renewal or next package': 'Discuss renewal',
};

function getButtonLabel(nudge) {
  if (nudge.suggestedAction === 'Schedule a check-in') {
    return `Book a follow-up meeting with ${nudge.contactName}`;
  }
  return ACTION_LABEL[nudge.suggestedAction] || nudge.suggestedAction;
}

// Map nudge type → the view to navigate to on card click. Exported for navTargets.test.js.
export const NUDGE_TARGET_VIEW = {
  overdue_invoice:      'Invoices',
  unsigned_contract:    'Contracts',
  unanswered_proposal:  'Contacts',
  no_contact:           'Contacts',
  package_expiring:     'Contacts',
};

function dispatchAction(nudge) {
  const { contactName, suggestedAction } = nudge;

  if (suggestedAction === 'Schedule a check-in') {
    window.dispatchEvent(new CustomEvent('aidey:open-chat', {
      detail: { message: `Book a follow-up meeting with ${contactName}` },
    }));
    return;
  }

  const messages = {
    'Chase payment': `Draft a payment chase email for ${contactName}`,
    'Send a gentle reminder': `Draft a follow-up for ${contactName} about their unsigned contract`,
    'Follow up on proposal': `Draft a follow-up for ${contactName} about their unsigned contract`,
    'Discuss renewal or next package': `Draft a renewal message for ${contactName}`,
  };
  const message = messages[suggestedAction] || `Help me follow up with ${contactName}`;
  window.dispatchEvent(new CustomEvent('aidey:open-chat', { detail: { message } }));
}

// ── the data ────────────────────────────────────────────────────────────────
// limit: how many contacts to fetch (the Dashboard asks for 3); total is always the true count.
export function useFollowUps({ limit = null, enabled = true } = {}) {
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(enabled);
  // THE CASE THAT NAMED THE SHAPE (Carla, 2026-10-03). A failed load left this list empty, and an
  // empty list rendered an all-clear: a failure producing a reassurance.
  const [loadFailed, setLoadFailed] = useState(false);

  const reload = useCallback(() => {
    if (!enabled) return Promise.resolve();
    return api(`/api/follow-up/nudges${limit ? `?limit=${limit}` : ''}`)
      .then(r => r.json())
      .then(data => {
        // A 200 carrying no groups array is a failure, not an empty list.
        if (Array.isArray(data.groups) && Number.isInteger(data.total)) {
          setGroups(data.groups); setTotal(data.total); setLoadFailed(false);
        } else setLoadFailed(true);
      })
      .catch(() => setLoadFailed(true))
      .finally(() => setLoading(false));
  }, [limit, enabled]);

  useEffect(() => { reload(); }, [reload]);

  // Carla's item 10 (2026-10-12), replacing decision 45's 7-day snooze: the ✕ dismisses that card's
  // follow-ups for good. The card goes at once and the toast says "Dismissed" with Undo; the
  // request is sent only once the Undo window has passed (useDelayedDelete, the one delayed-undo
  // pattern), and it names the card's items (their itemKeys), so a new follow-up for the same
  // client still appears. A refusal brings the card back with the server's own words.
  const delayed = useDelayedDelete({
    send: (item, { keepalive }) => api('/api/follow-up/dismiss', {
      method: 'POST',
      body: JSON.stringify({ contactId: item.contactId, itemKeys: item.itemKeys }),
      keepalive,
    }),
    onDeleted: () => { reload(); },   // the Dashboard's top 3 refills from the next contact
    describe: () => 'Dismissed',
    failMessage: 'That follow-up could not be dismissed. Please try again.',
  });
  const dismiss = useCallback((group) => {
    delayed.remove({
      id: group.contactId,
      contactId: group.contactId,
      itemKeys: (group.reasons || []).map((r) => r.itemKey).filter(Boolean),
    });
  }, [delayed]);
  const shown = groups.filter((g) => !delayed.isHidden(g.contactId));
  const hiddenCount = groups.length - shown.length;

  return { groups: shown, total: Math.max(0, total - hiddenCount), loading, loadFailed, dismiss, reload };
}

// ── one contact, every reason ───────────────────────────────────────────────
export function FollowUpCard({ group, onNavigate, onOpenContact, onDismiss }) {
  const top = group.reasons[0];
  const color = URGENCY_COLOR[top?.urgency] || URGENCY_COLOR.low;
  const targetView = NUDGE_TARGET_VIEW[top?.type];
  const open = () => {
    if (onOpenContact) return onOpenContact(group.contactId);
    if (!onNavigate || !targetView) return;
    // Block 203a: publish WHICH contact before switching view, so Contacts opens it.
    if (targetView === 'Contacts' && group.contactId) setDeepLink('contact', group.contactId);
    onNavigate(targetView);
  };
  const clickable = !!(onOpenContact || (onNavigate && targetView));
  return (
    <div
      data-testid="followup-card"
      onClick={clickable ? open : undefined}
      title={clickable ? `Open ${group.contactName || targetView}` : undefined}
      className={`dash-fu${clickable ? ' dash-fu--open' : ''}`}
    >
      <div className="dash-fu__head">
        <div className="dash-fu__who">
          <NudgeWarningIcon color={color} />
          <span className="dash-fu__name">{group.contactName}</span>
          {clickable && <span className="dash-fu__arrow" aria-hidden="true">↗</span>}
        </div>
        {onDismiss && (
          <button
            type="button"
            onClick={e => { e.stopPropagation(); onDismiss(group); }}
            title="Dismiss"
            aria-label={`Dismiss the follow-up for ${group.contactName}`}
            // A 44px tap area around a small ×, as the addendum requires (dash-fu__x).
            className="dash-fu__x"
          >
            ×
          </button>
        )}
      </div>
      {group.reasons.map(r => (
        <div key={r.itemKey || r.type} data-testid="followup-reason" className="dash-fu__reason" style={{ display: 'flex', flexWrap: 'nowrap' }}>
          {group.reasons.length > 1 && <span className="dash-fu__rdot" style={{ background: URGENCY_COLOR[r.urgency] || URGENCY_COLOR.low }} />}
          {/* nowrap above + flex 1 here: a global mobile rule set flex-wrap: wrap on this row, so on a
              phone the text dropped onto its own line under the dot (seen in the fix(toast) shots). */}
          <span style={{ flex: '1 1 auto', minWidth: 0, overflowWrap: 'anywhere' }}>{r.reason}</span>
        </div>
      ))}
      <div className="dash-fu__act">
        <Button variant="confirm" size="sm" onClick={e => { e.stopPropagation(); dispatchAction(top); }}>
          {getButtonLabel(top)}
        </Button>
      </div>
    </div>
  );
}

// The four states every follow-up surface shows, in this order: loading, FAILED (never read as
// empty), empty (a dashed box with one action), or the cards.
function FollowUpBody({ state, children, onOpenContacts }) {
  if (state.loading) return <Skeleton lines={3} label="Loading follow-ups" />;
  if (state.loadFailed) {
    return (
      <p data-testid="followup-load-failed" className="dash-failed" role="alert">
        This could not be loaded, so Aidey does not know who needs following up.
        It does not mean everyone is up to date.
      </p>
    );
  }
  if (state.groups.length === 0) {
    return (
      <div data-testid="followup-empty" className="ui-empty dash-empty">
        <span className="ui-empty__text">No one needs a follow-up right now</span>
        {onOpenContacts && (
          <Button variant="confirm" size="sm" onClick={onOpenContacts}>Open contacts</Button>
        )}
      </div>
    );
  }
  return children;
}

/**
 * FollowUpPanel — the Dashboard's "Follow-up needed" section.
 * The top 3 contacts (decision 42), "See all (N)" when there are more (decision 43). Always shown
 * in a business workspace: it used to vanish when empty, while loading AND when the load failed.
 *   onNavigate(view) — Dashboard's setFilter
 */
export default function FollowUpPanel({ onNavigate }) {
  const state = useFollowUps({ limit: 3 });
  const seeAll = () => { setDeepLink('contact_filter', 'follow-up'); if (onNavigate) onNavigate('Contacts'); };
  return (
    <DashboardCard tint="pastel-yellow" title="Follow-up needed"
      meta={!state.loading && !state.loadFailed && state.total > 0 ? (
        <span className="dash-count dash-count--ink" aria-label={`${state.total} in all`}>{state.total}</span>
      ) : null}
      actions={!state.loading && !state.loadFailed && state.total > 3 ? (
        <Button data-testid="followup-see-all" variant="create" size="sm" onClick={seeAll}>See all ({state.total})</Button>
      ) : null}>
      <FollowUpBody state={state} onOpenContacts={onNavigate ? () => onNavigate('Contacts') : null}>
        <div className="dash-fu-list">
          {state.groups.slice(0, 3).map(g => (
            <FollowUpCard key={g.contactId} group={g} onNavigate={onNavigate} onDismiss={state.dismiss} />
          ))}
        </div>
      </FollowUpBody>
    </DashboardCard>
  );
}

/**
 * FollowUpList — every contact that needs a follow-up, for the Contacts page's chip (decision 43).
 * Takes the state from the page's own useFollowUps(), which also gives the chip its count.
 *   onOpenContact(id) — opens the contact on the Contacts page
 *   search — the page's search text, matched like the page's own search: name, email, company or tag
 *   contacts — the page's loaded contacts (the follow-up groups carry only the name)
 */
export function FollowUpList({ state, onOpenContact, search = '', contacts = [] }) {
  const q = search.trim().toLowerCase();
  const byId = new Map((contacts || []).map(c => [c.id, c]));
  const matches = (g) => {
    const c = byId.get(g.contactId) || {};
    return [g.contactName, c.name, c.email, c.company, ...(Array.isArray(c.tags) ? c.tags : [])]
      .some(v => String(v || '').toLowerCase().includes(q));
  };
  const shown = q ? state.groups.filter(matches) : state.groups;
  return (
    <div data-testid="followup-list">
      <FollowUpBody state={state}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))', gap: 'var(--space-3)' }}>
          {shown.map(g => (
            <FollowUpCard key={g.contactId} group={g} onOpenContact={onOpenContact} onDismiss={state.dismiss} />
          ))}
        </div>
        {q && shown.length === 0 && <p className="dash-text" style={{ textAlign: 'center', padding: 'var(--space-8) 0' }}>No follow-ups match your search.</p>}
      </FollowUpBody>
    </div>
  );
}
